import {useMemo, useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit, faPlus, faTrash} from "@fortawesome/free-solid-svg-icons";

// Utils
import {exists} from "../../utils/helpers";

// Components
import ModalDelete from "../../components/ModalDelete";
import Modal from "../../components/Modal";
import ModalChecksheetRestriction from "./ModalChecksheetRestriction";
import {RestrictionDetails} from "./ChecksheetRestrictions";
import Help from "../../components/Help";
import VisibilityConflictWarning from "./VisibilityConflictWarning";

// Style
import {voice} from "../../style/components/typography";
import {flex, z} from "../../style/components/mixins";
import {border, pad, radius} from "../../style/components/variables";
import {Button, Error, Heading, Inline, Pill} from "../../style/components/general";

const ModalChecksheetRestrictions = ({
  visible,
  setVisible,
  checksheetRestrictions,
  facilityId,
  handleSave,
  states,
  state,
  restrictTo = [],
  conflicts = []
}) => {
  const [target, setTarget] = useState();
  const [targetIdx, setTargetIdx] = useState();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);

  const useableStates = useMemo(() => {
    const keys = Object.keys(states);

    if (keys.length === 0) return {};
    return Object.fromEntries(
      keys.filter(key => states[key]?.builder?.allIds?.length > 0).map(key => [key, states[key]])
    );
  }, [states]);

  const handleEdit = (restriction, idx) => {
    setTargetIdx(idx);
    setTarget(restriction);
    setShowManageModal(true);
  };

  const handleDelete = idx => {
    setTargetIdx(idx);
    setShowDeleteModal(true);
  };

  const update = restriction => {
    if (target && exists(targetIdx)) {
      handleSave(
        checksheetRestrictions.map((r, i) => (i === targetIdx ? restriction : r)),
        targetIdx,
        "edit"
      );
    } else
      handleSave([...checksheetRestrictions, restriction], checksheetRestrictions.length, "add");
  };

  const filteredDefault = checksheetRestrictions
    .map((restriction, idx) => ({...restriction, idx}))
    .filter(restriction => {
      const {isState} = restriction;

      return !isState;
    });

  const filteredState = checksheetRestrictions
    .map((restriction, idx) => ({...restriction, idx}))
    .filter(restriction => {
      const {isState} = restriction;

      return isState;
    });

  if (showDeleteModal)
    return (
      <ModalDelete
        visible={visible && showDeleteModal}
        setVisible={setVisible}
        hasBackButton
        goBack={() => {
          setTargetIdx(undefined);
          setShowDeleteModal(false);
        }}
        confirmDelete={() => {
          handleSave(
            checksheetRestrictions.filter((_v, i) => i !== targetIdx),
            targetIdx,
            "delete"
          );
          setTargetIdx(undefined);
          setShowDeleteModal(false);
        }}
      />
    );

  if (showManageModal)
    return (
      <ModalChecksheetRestriction
        visible={visible && showManageModal}
        setVisible={setVisible}
        goBack={() => {
          setTarget(undefined);
          setShowManageModal(false);
        }}
        target={target}
        facilityId={facilityId}
        checksheetRestrictions={checksheetRestrictions}
        update={update}
        state={state}
        states={states}
        restrictTo={restrictTo}
      />
    );

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <HeaderWrapper>
        <ModalTitle>Checksheet Restrictions</ModalTitle>&nbsp;
        <Help title="Checksheet restrictions">
          A restriction limits a set of fields to only the listed users or roles &ndash;
          everyone else with access to the checksheet will not see those fields.
        </Help>
      </HeaderWrapper>

      <VisibilityConflictWarning conflicts={conflicts} />

      {(!state || filteredDefault?.length > 0) && (
        <>
          {state && <Pill quiet>DEFAULT</Pill>}
          {filteredDefault?.map(restriction => (
            <WrapperInline key={restriction.label}>
              <Summary>
                <strong>{restriction.label}</strong>
                <RestrictionDetails restriction={restriction} />
              </Summary>
              <Actions>
                <Option
                  type="button"
                  title="Edit"
                  onClick={() => handleEdit(restriction, restriction.idx)}
                  data-testid="condition.edit">
                  <FontAwesomeIcon icon={faEdit} />
                </Option>
                <Option
                  type="button"
                  title="Delete"
                  onClick={() => handleDelete(restriction.idx)}
                  data-testid="condition.delete">
                  <FontAwesomeIcon icon={faTrash} />
                </Option>
              </Actions>
            </WrapperInline>
          ))}
          <br />
        </>
      )}
      {state && filteredState?.length > 0 && (
        <>
          <Pill color={state?.color ? `#${state.color}` : undefined} quiet>
            {state.name}
          </Pill>
          {filteredState?.map(restriction => (
            <WrapperInline key={restriction.label}>
              <Summary>
                <strong>{restriction.label}</strong>
                <RestrictionDetails restriction={restriction} />
              </Summary>
              <Actions>
                <Option
                  type="button"
                  title="Edit"
                  onClick={() => handleEdit(restriction, restriction.idx)}
                  data-testid="condition.edit">
                  <FontAwesomeIcon icon={faEdit} />
                </Option>
                <Option
                  type="button"
                  title="Delete"
                  onClick={() => handleDelete(restriction.idx)}
                  data-testid="condition.delete">
                  <FontAwesomeIcon icon={faTrash} />
                </Option>
              </Actions>
            </WrapperInline>
          ))}
        </>
      )}

      {Object.keys(useableStates).length === 0 ? (
        <StyledError>
          Checksheet must have elements added in order to create a restriction.
        </StyledError>
      ) : (
        <Button
          type="button"
          onClick={() => {
            setShowManageModal(true);
          }}>
          <FontAwesomeIcon icon={faPlus} />
          &nbsp;Restriction
        </Button>
      )}
    </Modal>
  );
};

ModalChecksheetRestrictions.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  checksheetRestrictions: PropTypes.arrayOf(PropTypes.any),
  handleSave: PropTypes.func.isRequired,
  facilityId: PropTypes.number.isRequired,
  states: PropTypes.objectOf(PropTypes.any).isRequired,
  state: PropTypes.objectOf(PropTypes.any),
  restrictTo: PropTypes.arrayOf(PropTypes.any),
  conflicts: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string))
};

// Style Overrides
const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: ${pad}px 0;
`;

const ModalTitle = styled(Heading)`
  margin: 0;
`;

const Wrapper = styled.div`
  margin: ${pad}px 0 ${pad * 2}px;
`;

const WrapperInline = styled(Wrapper)`
  ${flex("row", "nowrap", "space-between", "flex-start")};
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  padding: ${pad}px;
`;

const Summary = styled.div`
  min-width: 0;
  flex: 1;
  padding-right: ${pad}px;
`;

const Actions = styled(Inline)`
  flex-shrink: 0;
`;

const Option = styled(Button)`
  font-weight: bold;
  z-index: ${z("above")};
  ${voice.normal}
`;

const StyledError = styled(Error)`
  margin-bottom: ${pad}px;
`;

export default ModalChecksheetRestrictions;
